import { createSlice } from "@reduxjs/toolkit";
import akuaService from "../../services/akuaService";

// Estado inicial
const initialState = {
  payments: [],
  selectedPayment: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 0,
  },
  filters: {
    status: "",
    created_at_from: "",
    created_at_to: "",
    payment_id: "",
    merchant_id: "",
    sort: "created_at:desc",
  },
};

// Slice
const akuaPagosSlice = createSlice({
  name: "akuaPagos",
  initialState,
  reducers: {
    // Estados de carga
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },

    // Pagos/Transacciones
    setPayments: (state, action) => {
      state.payments = action.payload;
      state.loading = false;
      state.error = null;
    },
    appendPayments: (state, action) => {
      state.payments = [...state.payments, ...action.payload];
      state.loading = false;
      state.error = null;
    },
    updatePaymentStatus: (state, action) => {
      const { paymentId, status } = action.payload;
      const paymentIndex = state.payments.findIndex((p) => p.id === paymentId);
      if (paymentIndex !== -1) {
        state.payments[paymentIndex].status = status;
      }
    },

    // Pago seleccionado
    setSelectedPayment: (state, action) => {
      state.selectedPayment = action.payload;
    },
    clearSelectedPayment: (state) => {
      state.selectedPayment = null;
    },

    // Paginación
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    // Filtros
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },

    // Reset
    resetState: (state) => {
      return initialState;
    },
  },
});

// Export actions
export const { setLoading, setError, clearError, setPayments, appendPayments, updatePaymentStatus, setSelectedPayment, clearSelectedPayment, setPagination, setFilters, clearFilters, resetState } =
  akuaPagosSlice.actions;

// Thunks (acciones asíncronas)

/**
 * Obtener listado de pagos/transacciones
 */
export const fetchPayments = (filters = {}, append = false) => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      const state = getState().akuaPagos;
      const mergedFilters = { ...state.filters, ...filters };

      // Limpiar filtros vacíos antes de enviar a la API
      const cleanFilters = {};
      Object.entries(mergedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          cleanFilters[key] = value;
        }
      });

      console.log("🔍 Filtros enviados a AKUA:", cleanFilters);

      const response = await akuaService.getPayments(cleanFilters);

      // Procesar los datos de pagos para adaptarlos a la estructura esperada por la UI
      const processedPayments = (response.data || []).map((payment) => ({
        id: payment.id,
        payment_id: payment.payment_id,
        status: payment.status,
        amount: payment.initial_amount,
        currency: payment.currency,
        created_at: payment.created_at * 1000, // Convertir timestamp Unix a milisegundos
        updated_at: payment.updated_at * 1000,
        external_client_trace_id: payment.external_client_trace_id,

        // Información del merchant
        merchant: {
          id: payment.merchant?.id,
          name: payment.merchant?.name,
          city: payment.merchant?.location_address?.city,
        },

        // Información del instrumento de pago
        instrument: {
          id: payment.instrument?.id,
          type: payment.instrument?.type,
          fingerprint: payment.instrument?.fingerprint,
          rail: payment.instrument?.rail,
          // Información de la tarjeta si existe
          card: payment.instrument?.card
            ? {
                last_four_digits: payment.instrument.card.last_four_digits,
                expiration_month: payment.instrument.card.expiration_month,
                expiration_year: payment.instrument.card.expiration_year,
                issuer_identification_number: payment.instrument.card.issuer_identification_number,
              }
            : null,
          // Dirección de facturación
          billing_address: payment.instrument?.user_data?.billing_address,
        },

        // Información de captura
        capture: payment.capture,

        // Transacciones asociadas
        transactions: payment.transactions || [],

        // Para compatibilidad con código existente
        customer: {
          name: payment.instrument?.user_data?.billing_address?.city || "N/A",
          email: payment.external_client_trace_id || "",
        },
      }));

      if (append) {
        dispatch(appendPayments(processedPayments));
      } else {
        dispatch(setPayments(processedPayments));
      }

      // Actualizar paginación
      if (response.pagination) {
        dispatch(
          setPagination({
            page: response.pagination.page,
            page_size: response.pagination.page_size,
            total: response.pagination.total,
            total_pages: Math.ceil(response.pagination.total / response.pagination.page_size),
          })
        );
      }

      // Actualizar filtros aplicados
      dispatch(setFilters(mergedFilters));
    } catch (error) {
      console.error("Error fetching payments:", error);
      dispatch(setError(error.message || "Error al obtener las transacciones"));
    }
  };
};

/**
 * Obtener detalles de un pago específico
 */
export const fetchPaymentById = (paymentId) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      const response = await akuaService.getPaymentById(paymentId);

      // Procesar la respuesta individual de pago según la nueva estructura
      const processedPayment = {
        id: response.id,
        payment_id: response.id, // Usar el mismo ID
        status: response.status,
        amount: response.initial_amount,
        current_amount: response.current_amount,
        currency: response.currency,
        country: response.country,
        // Convertir fechas ISO a timestamps
        created_at: response.created_at ? new Date(response.created_at).getTime() : null,
        updated_at: response.updated_at ? new Date(response.updated_at).getTime() : null,
        external_client_trace_id: response.external_client_trace_id,
        client_id: response.client_id,
        organization_id: response.organization_id,
        merchant_id: response.merchant_id,

        // Información del instrumento de pago (nueva estructura)
        payment_instrument: response.payment_instrument,

        // Información de captura
        capture: response.capture,

        // Transacciones asociadas (nueva estructura con campos adicionales)
        transactions: (response.transactions || []).map((transaction) => ({
          id: transaction.id,
          type: transaction.type,
          status: transaction.status,
          status_detail: transaction.status_detail,
          amount: transaction.amount,
          authorization_code: transaction.authorization_code,
          timestamp: transaction.timestamp ? new Date(transaction.timestamp).getTime() : null,
          created_at: transaction.created_at ? new Date(transaction.created_at).getTime() : null,
          updated_at: transaction.updated_at ? new Date(transaction.updated_at).getTime() : null,
          network_audit: transaction.network_audit,
        })),

        // Para compatibilidad con código existente
        customer: {
          name: response.payment_instrument?.type || "N/A",
          email: response.external_client_trace_id || "",
        },
      };

      dispatch(setSelectedPayment(processedPayment));
    } catch (error) {
      console.error("Error fetching payment details:", error);
      dispatch(setError(error.message || "Error al obtener los detalles del pago"));
    }
  };
};

/**
 * Procesar devolución
 */
export const processRefund = (paymentId, refundData = {}) => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      const result = await akuaService.cancelOrRefund(paymentId, refundData);

      // Actualizar el estado del pago en la lista
      dispatch(
        updatePaymentStatus({
          paymentId: paymentId,
          status: result.status || "REFUNDED",
        })
      );

      // Si tenemos el pago seleccionado, actualizarlo también
      const state = getState().akuaPagos;
      if (state.selectedPayment && state.selectedPayment.id === paymentId) {
        dispatch(
          setSelectedPayment({
            ...state.selectedPayment,
            status: result.status || "REFUNDED",
          })
        );
      }

      dispatch(setLoading(false));
      return result;
    } catch (error) {
      console.error("Error processing refund:", error);
      dispatch(setError(error.message || "Error al procesar la devolución"));
      throw error;
    }
  };
};

/**
 * Crear link de pago
 */
export const createPaymentLink = (paymentData) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      const result = await akuaService.createPaymentLink(paymentData);
      dispatch(setLoading(false));

      // Opcionalmente refrescar la lista de pagos después de crear el link
      // dispatch(fetchPayments());

      return result;
    } catch (error) {
      console.error("Error creating payment link:", error);
      dispatch(setError(error.message || "Error al crear el enlace de pago"));
      throw error;
    }
  };
};

/**
 * Refrescar lista de pagos
 */
export const refreshPayments = () => {
  return async (dispatch, getState) => {
    const state = getState().akuaPagos;
    return dispatch(fetchPayments(state.filters));
  };
};

/**
 * Cargar más pagos (paginación infinita)
 */
export const loadMorePayments = () => {
  return async (dispatch, getState) => {
    const state = getState().akuaPagos;
    const { pagination, filters } = state;

    if (pagination.page < pagination.total_pages) {
      const nextPageFilters = {
        ...filters,
        page: pagination.page + 1,
      };

      return dispatch(fetchPayments(nextPageFilters, true));
    }
  };
};

// Selectores
export const selectPayments = (state) => state.akuaPagos.payments;
export const selectSelectedPayment = (state) => state.akuaPagos.selectedPayment;
export const selectLoading = (state) => state.akuaPagos.loading;
export const selectError = (state) => state.akuaPagos.error;
export const selectPagination = (state) => state.akuaPagos.pagination;
export const selectFilters = (state) => state.akuaPagos.filters;

// Export reducer
export default akuaPagosSlice.reducer;
